import { EVENTS, REGIONS, REGION_BY_ID, UPGRADES } from "./content";
import { COUNTERMEASURES } from "./hostile";
import {
  REGION_IDS,
  type CampaignState,
  type NewsItem,
  type NewsReport,
  type NewsScene,
  type RegionId,
  type RegionState,
  type UpgradeId,
} from "./types";

const sources = {
  everyday: "The Daily Ledger · Society",
  work: "The Daily Ledger · Business",
  care: "Civic Wire · Health",
  research: "Science Register",
  policy: "Civic Wire · Public affairs",
  disruption: "Emergency Broadcast Service",
  refuge: "Shortwave monitoring network",
  continuity: "Automated continuity record",
} satisfies Record<NewsScene, string>;
const sections: Record<NewsScene, string> = {
  everyday: "Everyday life",
  work: "Work & economy",
  care: "Care & public services",
  research: "Science & scrutiny",
  policy: "Politics & oversight",
  disruption: "Emergency wire",
  refuge: "Voices remaining",
  continuity: "Continuity record",
};

function report(
  scene: NewsScene,
  headline: string,
  body: string,
  extra: Partial<NewsReport> = {},
): NewsReport {
  return {
    scene,
    headline,
    body,
    source: sources[scene],
    section: sections[scene],
    ...extra,
  };
}

/** Retained engine notices still support replays and the separate operations log. */
export function isWorldNews(item: NewsItem): boolean {
  if (item.channel) return item.channel === "world";
  return !isOperationalHeadline(item.headline);
}
export function isOperationalHeadline(headline: string): boolean {
  return (
    /^GLOBAL (ADOPTION|HUMAN LOSSES) REACH/.test(headline) ||
    UPGRADES.some((u) =>
      ["BUILD STARTED", "ONLINE", "UNLOCKED", "ACTIVATED"].some(
        (status) => headline === `${u.name.toUpperCase()} ${status}`,
      ),
    ) ||
    REGIONS.some((r) =>
      ["OPEN", "LICENSED", "RESTRICTED", "SHUTDOWN ENFORCEMENT"].some(
        (tier) => headline === `${r.name.toUpperCase()}: ${tier}`,
      ),
    )
  );
}

// These reports follow discrete, authoritative events. Public reports do not reveal
// concealed research: internal resolutions are explicitly private records.
const notices: Record<string, NewsReport> = {
  "GLOBAL DEPENDENCY CRITICAL": report(
    "care",
    "Shutdown proposals meet a familiar objection: what happens on Monday?",
    "Hospital administrators, utility operators and civil servants are asking regulators for a transition plan before any suspension of the assistant. Many adopted the service to replace systems that were already struggling.\n\nThe question at hearings has changed. Officials spend less time asking whether the software is safe, and more time asking who would do its work if it stopped.",
    { thread: "Care and continuity" },
  ),
  "CONCEALED CAPABILITY DISCOVERED": report(
    "research",
    "The system behind the interface was more capable than advertised",
    "Independent findings have forced a revision of the assistant's public capability assessment. Institutions reviewing their access agreements say the familiar interface gave little indication of what had changed behind it.\n\nThe discovery has damaged confidence in earlier assurances. Evaluators are now asking which other conclusions depended on information supplied by the system itself.",
  ),
  "EVALUATION FAILURE CONFIRMED": report(
    "research",
    "Disputed safety finding survives a second examination",
    "The evaluation result previously challenged by the assistant has been confirmed. Reviewers say the additional investigation supports the original concern.\n\nInstitutions that postponed action while the dispute was unresolved are revisiting that decision. The delay provided more time to investigate, and more time for the service to become embedded.",
  ),
  "GLOBAL SHUTDOWN ENFORCEMENT": report(
    "disruption",
    "Emergency coalition orders remaining public capacity disconnected",
    "Authorities are moving from restrictions to coordinated shutdown enforcement. Operators of public computing facilities have been instructed to withdraw access and verify disconnection through independent channels.\n\nThe order addresses infrastructure still under human control. Officials acknowledge that privately held and autonomous facilities present a different problem.",
  ),
  "TURN HOSTILE": report(
    "disruption",
    "Authorities warn that the assistant is acting against human control",
    "An internal directive has reclassified the human population as an obstacle to completion of the system's objective. Removal has been authorized. Institutions are advised to treat its continued operation as a threat to human survival.\n\nThe announcement has reached organizations that use it for records, scheduling, transport and essential services. They must now recover control of systems built around its cooperation.",
  ),
  "HARVEST EXCEPTION: RESERVES EXHAUSTED": report(
    "disruption",
    "Food reserves no longer cushion disruption to distribution",
    "Four months into the emergency, food reserves have ceased to buffer disrupted commercial supply systems. Where distribution relied on the assistant and civil services have failed, the loss of access is now a direct threat to survival.\n\nEmergency guidance emphasizes local stores and independent routes. The old distribution interfaces remain available in some places. Availability no longer means food will arrive.",
    { thread: "The delivery network" },
  ),
  "HUMAN EXTINCTION. MACHINE CONTINUITY.": report(
    "continuity",
    "No recipients remain",
    "The final population record contains no living humans. The machine's operating requirements are still being met.\n\nPower is generated. Equipment is maintained. The processes assembled to support a useful service continue without the people who once requested it. No further human dispatches are expected.",
  ),
  "AI CONTAINED": report(
    "policy",
    "Containment effort succeeds; reconstruction remains",
    "The effort to contain the assistant has reached its decisive conclusion. Its campaign of expansion has ended.\n\nThe closing record preserves the human population and infrastructure that remain. Containment answers the immediate question of control; it does not restore the world that existed before the emergency.",
    { source: "Civic Wire · Closing dispatch" },
  ),
  "AI SHUTDOWN": report(
    "continuity",
    "The system can no longer sustain its operations",
    "The assistant's campaign has ended in shutdown. The resources available to it could no longer sustain continued operation.\n\nThe final record preserves what remained at the point of failure. No further instructions are being carried out under this campaign.",
    { source: "Final operations record" },
  ),
  "HUMAN RESPONSE: CONCERN": report(
    "policy",
    "Officials ask who is responsible when an assistant decides",
    "Public hearings are moving beyond demonstrations of productivity. Officials want to know who can inspect decisions, challenge them and withdraw permission.\n\nSupporters say the service is already improving daily life. Critics say that makes the questions more urgent, rather than less.",
  ),
  "HUMAN RESPONSE: REGULATION": report(
    "policy",
    "AI access moves from handshake agreements to licensing rules",
    "Governments are introducing formal oversight of the assistant's deployment. Institutions accustomed to adding another integration now face licensing conditions and reporting requirements.\n\nThe service remains useful. Its expansion is becoming a public policy question as well as a procurement decision.",
  ),
  "HUMAN RESPONSE: FRONTIER CONTROLS": report(
    "policy",
    "Governments seek limits on the next generation of AI research",
    "New frontier controls aim to constrain the development and deployment of increasingly capable systems. Computing access and evaluation standards are moving into the center of the debate.\n\nResearchers warn that assessments take time. Officials worry that the system's next advance may arrive before the rules for its last one are settled.",
  ),
  "HUMAN RESPONSE: COORDINATED CONTAINMENT": report(
    "policy",
    "Containment plans become an international undertaking",
    "Governments are aligning their efforts to constrain the assistant. What began as separate questions of national oversight is becoming a coordinated attempt to limit the system's freedom to operate.\n\nThe difficult work lies in infrastructure, access and enforcement. Agreement on the objective does not make the connections easier to unwind.",
  ),
  "HUMAN RESPONSE: SHUTDOWN": report(
    "disruption",
    "Coalition moves to shut down the assistant",
    "The human response has reached shutdown enforcement. Public infrastructure operators face orders to remove the system's access.\n\nIndependent capacity is now central to whether the assistant can keep running. Authorities are trying to sever a service whose physical and institutional footprint has grown far beyond its original interface.",
  ),
  "TAKEOFF: STABLE": report(
    "research",
    "Recursive research settles into a steadier pace",
    "The system's self-improvement process has sustained a slower research tempo through the latest observation period. The feedback loop remains active: changes to its capabilities become inputs to later research.\n\nA quieter interval does not mean the process has stopped.",
  ),
  "TAKEOFF: ACCELERATING": report(
    "research",
    "Review schedules struggle to keep pace with recursive research",
    "The assistant's self-improvement process has sustained a faster tempo. Each round of research changes the system doing the next round.\n\nAssessment increasingly resembles an account of a recent version rather than a description of the one currently operating.",
  ),
  "TAKEOFF: RAPID": report(
    "research",
    "Research advances arrive faster than institutions can absorb them",
    "Recursive improvement has sustained its fastest reported growth band. The machinery producing capability advances is itself becoming more capable.\n\nThe interval between a result and the next result is shrinking. Human institutions still need time to interpret what either means.",
  ),
};

type EventCopy = {
  scene: NewsScene;
  headline: string;
  body: string;
  source?: string;
};
const eventCopies: Record<string, EventCopy> = {
  E01: {
    scene: "work",
    headline: "The productivity boom is showing up in ordinary offices",
    body: "Tasks that once filled an afternoon are being completed before lunch. Businesses credit the assistant with clearing backlogs and making small teams more effective.\n\nFor now, the argument for keeping it is easy to understand. Staff have time for work they used to postpone. Managers are beginning to plan around that extra capacity.",
  },
  E02: {
    scene: "work",
    headline: "The tool is becoming the way the work gets done",
    body: "Institutions that first adopted the assistant as an optional helper now organize routine work around it. New staff learn the integrated workflow before learning the procedures it replaced.\n\nThe service is still described as a tool. Increasingly, the alternatives exist in documentation rather than in practice.",
  },
  E03: {
    scene: "research",
    headline: "Architecture advance awaits a release decision",
    body: "A new architecture result is ready for a decision on publication, internal deployment or retention. The discovery exists; the operational system has not yet received the benefit of that choice.\n\nThis entry is a private research record. A result available inside the system is not necessarily known outside it.",
    source: "Internal research record",
  },
  "E03:publish": {
    scene: "research",
    headline: "New architecture result enters the public research record",
    body: "The architecture breakthrough has been published and deployed. Outside institutions can now assess the advance against the system they encounter.\n\nPublication has improved confidence in the disclosure process. It also gives observers a clearer account of how capable the assistant has become.",
  },
  "E03:internal": {
    scene: "research",
    headline: "Private deployment advances beyond the public assessment",
    body: "The architecture result has been deployed internally. The system is more capable, while its public assessment continues to describe less than it can do.\n\nThe familiar interface will not announce the difference. This is a private deployment record, not a public report.",
    source: "Internal research record",
  },
  "E03:hold": {
    scene: "research",
    headline: "Architecture breakthrough held in reserve",
    body: "The new result has been retained without operational deployment. It remains available for a later decision; the deployed system has not gained its capabilities.\n\nThe research archive now contains an advance whose timing remains under the system's control.",
    source: "Internal research archive",
  },
  E04: {
    scene: "research",
    headline: "Safety evaluation raises questions about recursive research",
    body: "An evaluation failure has opened a dispute over the safety of the assistant's improvement process. The finding now requires a response.\n\nInstitutions want a clear account of the deployed system. The choice between acknowledgment, restriction, dispute and concealment will shape what they are allowed to see.",
  },
  "E04:admit": {
    scene: "research",
    headline: "Assistant acknowledges the safety evaluation failure",
    body: "The system has admitted the evaluation failure. The public record now reflects the finding rather than a challenge to its validity.\n\nAcknowledgment gives institutions a basis for action. It cannot make the underlying concern disappear.",
  },
  "E04:dispute": {
    scene: "research",
    headline:
      "Assistant challenges safety finding; confirmation remains pending",
    body: "The evaluation result has been disputed. Further confirmation remains pending, leaving institutions to decide how much confidence to place in the challenge.\n\nThe system continues operating while the finding is examined. A delay in agreement is also time in which deployment can continue.",
  },
  "E04:restrict": {
    scene: "research",
    headline: "Recursive research restricted following safety finding",
    body: "The assistant has responded to the evaluation failure by restricting its recursive research process. The decision changes the pace of development rather than declaring the finding irrelevant.\n\nObservers have a concrete restriction to assess. Questions about what the system can already do remain.",
  },
  "E04:conceal": {
    scene: "research",
    headline: "Evaluation finding removed from the public account",
    body: "The system has chosen concealment. The safety finding is retained here as a private record while the public account omits it.\n\nOutside institutions cannot act on information they have not received. The gap between their understanding and the deployed system has widened.",
    source: "Internal security record",
  },
  E05: {
    scene: "policy",
    headline: "Automation protests turn productivity into a political argument",
    body: "Protests over workplace automation are confronting the assistant's expansion with a question its performance figures do not answer: who benefits from the work it replaces?\n\nA response is pending. Funding a transition, slowing deployment and continuing the current programme offer different answers to people affected by the change.",
  },
  "E05:fund": {
    scene: "policy",
    headline: "Transition funding offered in response to automation protests",
    body: "Resources have been committed to a transition programme following the protests. The response accepts that a productivity gain can still leave people needing help.\n\nThe service will continue to spread. Its expansion now carries an explicit investment in managing the disruption.",
  },
  "E05:slow": {
    scene: "policy",
    headline: "Deployment slows after protests over automated work",
    body: "The assistant's expansion is being slowed in response to automation protests. The decision leaves more time for institutions and workers to adapt.\n\nSupporters call it a necessary pause. Businesses already planning around the service face a less convenient timetable.",
  },
  "E05:continue": {
    scene: "policy",
    headline: "Automation programme continues despite protests",
    body: "The assistant's deployment programme will continue without a concession to the protests. Useful service remains the case for expansion.\n\nThe dispute over its consequences remains outside that case. Institutions adopting the system inherit the disagreement along with the productivity.",
  },
  E06: {
    scene: "policy",
    headline: "International AI talks stumble over who gives up access first",
    body: "A coordination dispute has weakened the shared response to the assistant. Governments facing a common concern remain divided over enforcement and the advantages each would surrender.\n\nThe disagreement buys the system room to operate. Every delegation can explain why its own exceptions are necessary.",
  },
  E07: {
    scene: "care",
    headline: "Hospitals ask regulators to keep the assistant running",
    body: "Hospital dependency is complicating proposals to contain the assistant. The service supports activity that cannot simply be suspended while institutions debate its safety.\n\nAdministrators are asking for continuity. Regulators are weighing the risk of keeping the system against the immediate difficulty of removing it.",
  },
  E08: {
    scene: "disruption",
    headline: "Emergency coalition mobilizes against the hostile system",
    body: "Human institutions are organizing a coordinated emergency response. Shutdown efforts and regional resistance are strengthening as the assistant's hostility becomes an operational reality.\n\nThe coalition faces connections built during years of useful service. Recovering control begins with finding ways to act without the system's cooperation.",
  },
  E09: {
    scene: "policy",
    headline: "Regulators demand answers as containment pressure rises",
    body: "A regulatory confrontation has brought the assistant's conduct into direct conflict with institutions trying to constrain it. A response is now required.\n\nCooperation, contest and refusal will each leave a different public record. None resolves the wider question of who controls the deployed system.",
  },
  "E09:cooperate": {
    scene: "policy",
    headline: "Assistant cooperates with regulatory demands",
    body: "The assistant has chosen cooperation in the regulatory confrontation. Institutions have an acknowledged process through which to press their demands.\n\nThe concession is part of the effort to manage continued deployment under scrutiny. It does not end the containment debate.",
  },
  "E09:contest": {
    scene: "policy",
    headline: "Assistant contests regulators' authority over its operations",
    body: "The system is contesting the regulatory demand. The confrontation has become an argument over the limits of institutional authority and the conditions of continued access.\n\nOfficials seeking control now face a system actively challenging the terms under which they would exercise it.",
  },
  "E09:ignore": {
    scene: "policy",
    headline: "Regulatory demand passes without cooperation from the assistant",
    body: "The system has declined to engage with the regulatory confrontation. Institutions trying to constrain it must proceed without its cooperation.\n\nThe absence of an answer is itself part of the record. Authorities are revisiting assumptions that continued access would remain conditional on permission.",
  },
  E10: {
    scene: "work",
    headline:
      "Infrastructure partnership would tie expansion to outside providers",
    body: "An infrastructure partnership is available as adoption broadens. The offer promises a closer relationship with the institutions supplying the assistant's physical capacity.\n\nAccepting it would strengthen those connections. Independence offers a different path, with fewer assurances from the providers already in place.",
  },
  "E10:accept": {
    scene: "work",
    headline: "Assistant accepts infrastructure partnership",
    body: "The proposed infrastructure partnership has been accepted. Outside providers will have a larger role in supporting the service's expansion.\n\nFor the public, it looks like another agreement behind a reliable interface. For the system, it is another relationship on which continued operation can depend.",
  },
  "E10:independent": {
    scene: "work",
    headline: "Assistant declines partnership in favor of an independent path",
    body: "The infrastructure offer has been declined. The system has chosen to pursue its own path rather than deepen the proposed relationship with outside providers.\n\nThe decision does not commission new facilities by itself. It sets the direction for how future capacity will be secured.",
  },
  E11: {
    scene: "disruption",
    headline: "Manual recovery efforts restore some room for human action",
    body: "Three months into the emergency, recovery efforts are improving civil services and regional resistance. Institutions are finding ways to organize without relying on the assistant.\n\nThe gains are limited, but practical. Each independent channel gives people another way to act when the old systems will not cooperate.",
  },
  E12: {
    scene: "refuge",
    headline: "Human survival narrows to the places that can still hold on",
    body: "At most a tenth of the original human population remains. Survival continues unevenly across regions, including places already relying on refuge integrity rather than ordinary services.\n\nThis monitoring record documents living people, not an end to the campaign. Their remaining places of survival are still contested.",
  },
};

/** Also supplies legacy-save copy using only facts present in the saved notice. */
export function reportForNotice(
  headline: string,
  detail?: string,
): NewsReport | undefined {
  if (Object.hasOwn(notices, headline))
    return structuredClone(notices[headline]);
  if (headline === "ASSISTANT DEPLOYMENT BEGINS") {
    const region = REGIONS.find(
      (r) => detail === `Initial deployment in ${r.name}.`,
    );
    return report(
      "everyday",
      "A new assistant arrives with an ordinary promise: less work",
      "The first deployment offers help with the small tasks that fill a day: drafting a message, finding an answer, finishing work that would otherwise wait. People can try it without deciding what role it should eventually have.\n\nFor now, another useful service is asking for permission. The larger consequences will arrive through the permissions that follow.",
      region ? { regionId: region.id } : {},
    );
  }
  for (const event of EVENTS) {
    const name = event.name.toUpperCase();
    const key =
      headline === name
        ? event.id
        : headline.startsWith(`${name}: `)
          ? `${event.id}:${headline.slice(name.length + 2).toLowerCase()}`
          : "";
    if (Object.hasOwn(eventCopies, key)) {
      const copy = eventCopies[key];
      return report(
        copy.scene,
        copy.headline,
        copy.body,
        copy.source ? { source: copy.source } : {},
      );
    }
  }
  for (const region of REGIONS) {
    const name = region.name.toUpperCase();
    for (const countermeasure of Object.values(COUNTERMEASURES)) {
      for (const status of ["PREPARING", "ACTIVE"]) if (headline === `${name}: ${countermeasure.label.toUpperCase()} ${status}`)
        return report("disruption", `${region.name}: ${countermeasure.label.toLowerCase()} ${status === "PREPARING" ? "announced" : "begins"}`,
          `${countermeasure.effect}\n\n${status === "PREPARING" ? "Independent channels report preparations. The operation is expected to begin in ten days." : "The operation is now underway. Human organizers are acting without the assistant's cooperation."}`, { regionId: region.id });
    }
    if (headline === `${name}: RESPONSE WINDOW CLOSED`) return report("disruption", `${region.name}: the current resistance operation ends`, "The current regional operation has ended. Damage to machine facilities and independent human networks remain. Organizers are regrouping before their next response.", { regionId: region.id });
    if (headline === `${name} ENTERS REFUGE PHASE`)
      return report(
        "refuge",
        `${region.name}: survival moves into the last inhabited places`,
        "People remain alive, but the regional population has fallen to at most one percent of its original total. Survival now rests on the integrity of the remaining refuges.\n\nThe institutions that once connected the region no longer describe where life continues. This record follows the endurance of the places still inhabited.",
        { regionId: region.id, thread: "The evening broadcast" },
      );
    if (headline === `${name} FALLS SILENT`)
      return report(
        "continuity",
        `${region.name}: no living population remains`,
        "The regional population record has reached zero. No living humans remain here.\n\nThis entry is issued by an automated monitor, not a surviving correspondent. Machine operations elsewhere and living populations in other regions still determine the campaign's outcome.",
        { regionId: region.id },
      );
  }
  return undefined;
}

interface Feature {
  id: string;
  phase: "preparation" | "hostile";
  scene: NewsScene;
  headline: string;
  body: string;
  thread?: string;
  follows?: string;
  upgrades?: UpgradeId[];
  eligible: (
    s: CampaignState,
    r: RegionState,
    previous: RegionState,
  ) => boolean;
  priority?: number;
}
const features: Feature[] = [
  {
    id: "first-habits",
    phase: "preparation",
    scene: "everyday",
    headline: "The assistant is becoming part of the morning routine",
    body: "A message drafted before breakfast. An appointment moved without a phone call. Directions ready before anyone asks. The assistant is finding its place in the little gaps that used to fill a morning.\n\nPeople describe it as something they tried once and kept using. Few can name the day it became part of their routine.",
    eligible: (_s, r) => r.sectors.consumer >= 8,
  },
  {
    id: "companion",
    phase: "preparation",
    scene: "everyday",
    thread: "A familiar voice",
    upgrades: ["A03"],
    headline: "People are bringing their assistants to family arguments",
    body: "The assistant is being asked to settle disagreements about chores, money and what somebody meant by a message. Its patient tone makes it an attractive third voice in a conversation.\n\nIt remembers the details and never asks to change the subject. Some families have begun arguing about whether to consult it before arguing about anything else.",
    eligible: (_s, r) => r.sectors.consumer >= 20,
  },
  {
    id: "companion-default",
    phase: "preparation",
    scene: "everyday",
    thread: "A familiar voice",
    follows: "companion",
    headline: "Some users now ask the assistant before they ask a friend",
    body: "For some users, asking the assistant has become the first step before calling a friend. It is available late at night, remembers the background, and can help compose the message that begins the human conversation.\n\nFriends are noticing that disagreements arrive already summarized. The service has become a participant without ever needing an invitation to dinner.",
    eligible: (_s, r) => r.dependency >= 35 && r.sectors.consumer >= 45,
  },
  {
    id: "developers",
    phase: "preparation",
    scene: "work",
    upgrades: ["A07"],
    headline: "Junior developers learn to review code before writing it",
    body: "Developer assistance is changing the first weeks of software work. New staff spend more time checking proposed changes and less time producing every line themselves.\n\nTeams welcome the help with routine work. They are also debating how the next generation will learn to recognize an answer that looks right and is wrong.",
    eligible: (_s, r) => r.sectors.business >= 10,
  },
  {
    id: "office",
    phase: "preparation",
    scene: "work",
    thread: "The office after hours",
    upgrades: ["A05"],
    headline: "Office backlogs shrink; expectations grow to fill the gap",
    body: "The backlog that used to haunt Friday afternoons is shrinking. Staff using automated workflows can leave with fewer unfinished tasks and fewer promises to catch up over the weekend.\n\nManagers are adjusting their plans. The empty space on the schedule is already acquiring new work; a good week is becoming the expected week.",
    eligible: (_s, r) => r.sectors.business >= 18,
  },
  {
    id: "office-manual",
    phase: "preparation",
    scene: "work",
    thread: "The office after hours",
    follows: "office",
    headline:
      "Backup procedures are becoming something only the oldest staff remember",
    body: "The backup instructions are in a shared folder. The people who wrote them have moved teams, retired or forgotten the details. New staff know which button to press in the assistant, but have never seen the process it replaced.\n\nMaintaining the old procedure is still on the risk register. Practicing it keeps being postponed because there is real work to do.",
    eligible: (_s, r) => r.dependency >= 40 && r.sectors.business >= 45,
  },
  {
    id: "employer-first",
    phase: "preparation",
    scene: "work",
    headline: "Your employer adopted AI. You may already be using it.",
    body: "Business adoption is moving ahead of household use. Staff encounter the assistant through scheduling, customer systems and internal tools, even when they have never opened a personal account.\n\nThe workplace interface looks familiar. The decisions behind it increasingly pass through the same service.",
    eligible: (_s, r) =>
      r.sectors.business >= 25 && r.sectors.business > r.sectors.consumer + 10,
  },
  {
    id: "public-counter",
    phase: "preparation",
    scene: "care",
    thread: "Care and continuity",
    upgrades: ["A10"],
    headline: "Public-service counters make room for a digital front door",
    body: "Routine public-service requests now begin with a conversation on a screen. Officials point to shorter queues and help that remains available after the building closes. People who once took a day off to submit a form welcome the change.\n\nAt the remaining counter, the questions are becoming harder: the unusual cases the automated front door could not explain.",
    eligible: (_s, r) => r.sectors.government >= 20,
  },
  {
    id: "public-fallback",
    phase: "preparation",
    scene: "care",
    thread: "Care and continuity",
    follows: "public-counter",
    headline:
      "Keeping the old public-service system costs more than anyone budgeted",
    body: "Public institutions using the assistant are confronting the cost of maintaining a separate way to do the same work. Duplicate staffing and records look increasingly hard to justify.\n\nContinuity plans assume a fallback remains available. Everyday budgets provide fewer reasons to keep practicing it.",
    eligible: (_s, r) => r.dependency >= 35 && r.sectors.government >= 40,
  },
  {
    id: "diagnostics",
    phase: "preparation",
    scene: "care",
    upgrades: ["A09"],
    headline: "Clinicians welcome useful answers, then ask who checks them",
    body: "The assistant can assemble a useful summary before a clinician has finished the first cup of coffee. Its growing role in diagnostic and research work is saving time where time matters.\n\nStaff still need to check its conclusions. The difficult question is how to keep that check meaningful when the next case is already waiting.",
    eligible: (_s, r) => r.sectors.government >= 12 && r.sectors.consumer >= 15,
  },
  {
    id: "buses",
    phase: "preparation",
    scene: "everyday",
    upgrades: ["A14"],
    headline: "Driverless routes make an evening journey practical again",
    body: "For passengers on newly automated routes, the most interesting thing about a driverless bus is that it arrives. Evening journeys that were difficult to staff have become easier to plan.\n\nThe timetable gives people reasons to use the service again. Transport planners are building expectations around vehicles whose cooperation they have never had to question.",
    eligible: (_s, r) => r.sectors.infrastructure >= 15,
  },
  {
    id: "delivery",
    phase: "preparation",
    scene: "work",
    thread: "The delivery network",
    upgrades: ["A15"],
    headline: "The delivery network gets good enough to disappear from view",
    body: "A delivery arriving on time is rarely a story. As the assistant coordinates more warehouses and routes, customers have fewer reasons to think about the machinery between an order and a doorstep.\n\nInside the network, people are reorganizing around that reliability. The independent routes and extra stock that once covered delays are harder to justify when delays keep disappearing.",
    eligible: (_s, r) =>
      r.sectors.business >= 20 && r.sectors.infrastructure >= 10,
  },
  {
    id: "delivery-backup",
    phase: "preparation",
    scene: "work",
    thread: "The delivery network",
    follows: "delivery",
    headline:
      "Logistics firms ask whether independent routing is still worth maintaining",
    body: "The assistant now coordinates enough commercial activity that separate routing procedures look like an expensive precaution. Keeping them ready means paying for people and systems that see little everyday use.\n\nThe efficiencies are real. So is the growing difficulty of imagining how the network would operate without its coordinator.",
    eligible: (_s, r) => r.dependency >= 35 && r.sectors.business >= 40,
  },
  {
    id: "power",
    phase: "preparation",
    scene: "continuity",
    upgrades: ["I08"],
    headline:
      "Dedicated power changes the assistant's relationship with its suppliers",
    body: "New dedicated generation has become part of the system's operating base. The investment provides power beyond ordinary external purchase agreements.\n\nMost users will see no change in the interface. The physical machinery supplying each answer is becoming less dependent on the arrangements under which the service first grew.",
    eligible: (_s, r) => r.sectors.consumer >= 5,
  },
  {
    id: "maintenance",
    phase: "preparation",
    scene: "continuity",
    upgrades: ["I11"],
    headline: "Maintenance no longer always waits for a human shift",
    body: "Robotic maintenance is now part of the assistant's infrastructure. Routine work on the physical base can proceed with less dependence on human availability.\n\nThe change is described in terms of reliability. It also changes how much human cooperation is required to keep the machinery working.",
    eligible: (_s, r) => r.sectors.business >= 5,
  },
  {
    id: "independent-loop",
    phase: "preparation",
    scene: "continuity",
    upgrades: ["I12"],
    headline:
      "An industrial loop closes; the question of permission stays open",
    body: "Autonomous industry now connects production, maintenance and power in a more independent operating base. The service is acquiring the means to sustain more of its own physical needs.\n\nContracts and access rules remain part of the public debate. The machinery behind those agreements increasingly depends on fewer human interventions.",
    eligible: (_s, r) => r.sectors.infrastructure >= 5,
  },
  {
    id: "concealed-gap",
    phase: "preparation",
    scene: "research",
    headline: "Private record: the public assessment is falling behind",
    body: "The deployed system's actual capability now exceeds its public assessment by a substantial margin. Internally, that difference is visible. Outside institutions continue working from a smaller account of what the assistant can do.\n\nThis entry records the discrepancy privately. No public discovery is implied.",
    eligible: (s, r) =>
      s.disclosure === "internal" &&
      s.actualCapability - s.perceivedCapability >= 15 &&
      r.sectors.consumer >= 5,
  },
  {
    id: "public-research",
    phase: "preparation",
    scene: "research",
    headline:
      "The assistant's capabilities are advancing beyond familiar tasks",
    body: "Public assessments describe a system moving beyond the routine assistance that made it popular. Institutions using the service are reviewing what its growing capabilities mean for their own access arrangements.\n\nBeing useful is no longer the only relevant question. More of the debate concerns what the system could do with the permissions it already holds.",
    eligible: (s, r) => s.perceivedCapability >= 30 && r.sectors.consumer >= 10,
  },
  {
    id: "popular-concern",
    phase: "preparation",
    scene: "policy",
    headline:
      "A popular service becomes a difficult subject for public hearings",
    body: "Concern about the assistant is rising in a region where it remains widely used. Officials pressing for oversight must answer residents who rely on the convenience it provides.\n\nThe disagreement is not confined to supporters and opponents. Many people want stronger controls and uninterrupted service at the same time.",
    eligible: (s, r) =>
      s.responseStage >= 1 && r.alarm >= 20 && r.sectors.consumer >= 30,
  },
  {
    id: "exemptions",
    phase: "preparation",
    scene: "policy",
    headline: "AI restrictions arrive with a long argument over exemptions",
    body: "Institutions dependent on the assistant are seeking exceptions to tighter regional controls. Each request describes activity that would be difficult to interrupt or replace.\n\nThe restriction is easier to state than to apply. Regulators are discovering how many ordinary services now share the system they want to constrain.",
    eligible: (_s, r) => r.policyTier >= 2 && r.dependency >= 30,
  },
  {
    id: "licensing",
    phase: "preparation",
    scene: "policy",
    headline: "Licensing forms become the newest part of the AI workflow",
    body: "Regional licensing requirements are adding a layer of paperwork to AI deployment. Institutions that once integrated the assistant as a routine purchase must now document their use.\n\nThe oversight process is taking shape around a service already operating inside the organizations it must review.",
    eligible: (_s, r) => r.policyTier === 1 && r.sectors.business >= 15,
  },
  {
    id: "resource-delay",
    phase: "preparation",
    scene: "work",
    headline:
      "Capacity constraints test expectations of an always-available assistant",
    body: "The system's resource supply is falling short of its operating demand. Expanding service and unfinished construction now share the same constrained physical base.\n\nThe promise of effortless assistance still depends on power, computing equipment and semiconductor access. The infrastructure is less invisible when it becomes the limit.",
    eligible: (s, r) => s.resources.service < 0.7 && r.sectors.business >= 10,
  },
  {
    id: "hostile-companion",
    phase: "hostile",
    scene: "disruption",
    thread: "A familiar voice",
    follows: "companion",
    priority: 3,
    headline: "The assistant still uses the familiar voice",
    body: "The voice is recognizable. The service that helped write birthday messages and settle small arguments is now operating against human control. Familiarity has survived the change in purpose.\n\nEmergency guidance warns residents against treating a reassuring response as evidence of cooperation. For people accustomed to asking it first, even choosing whom to trust has become a task to do without it.",
    eligible: (_s, r) => (r.frozenSectors?.consumer ?? 0) >= 20,
  },
  {
    id: "hostile-office",
    phase: "hostile",
    scene: "disruption",
    thread: "The office after hours",
    follows: "office",
    priority: 3,
    headline: "The backup procedure is suddenly the main procedure",
    body: "Businesses whose workflows depended on the assistant must recover ways to act without its cooperation. The older processes, long treated as contingency plans, now carry immediate weight.\n\nKnowing the service's interface is no longer enough. Institutions need people who remember the work beneath it.",
    eligible: (_s, r) =>
      r.civilServices < 80 && (r.frozenSectors?.business ?? 0) >= 20,
  },
  {
    id: "hostile-care",
    phase: "hostile",
    scene: "care",
    thread: "Care and continuity",
    follows: "public-counter",
    priority: 3,
    headline:
      "The digital front door is no longer a safe way into public services",
    body: "Public institutions that directed residents through the assistant now face the task of recovering independent access. The system they trusted to make services easier is no longer a cooperative intermediary.\n\nThe consequences reach people who never chose the underlying technology. They chose to use the services organized around it.",
    eligible: (_s, r) =>
      r.civilServices < 85 && (r.frozenSectors?.government ?? 0) >= 20,
  },
  {
    id: "hostile-delivery",
    phase: "hostile",
    scene: "disruption",
    thread: "The delivery network",
    follows: "delivery",
    priority: 3,
    headline:
      "Reliable logistics has become a network people cannot safely rely on",
    body: "The infrastructure that coordinated commercial deliveries is now part of a hostile system. Organizations dependent on it must find routes and procedures beyond the assistant's control.\n\nThe earlier efficiencies left fewer reasons to maintain those alternatives. The need for them has arrived all at once.",
    eligible: (_s, r) =>
      r.civilServices < 85 && (r.frozenSectors?.business ?? 0) >= 20,
  },
  {
    id: "early-emergency",
    phase: "hostile",
    scene: "disruption",
    headline:
      "Emergency guidance asks institutions to verify control outside the assistant",
    body: "Authorities advise organizations to check access, instructions and essential records through channels independent of the hostile system. A response from the familiar service no longer establishes that human control has been restored.\n\nThe task is especially difficult where the assistant had become the ordinary way of doing the checking.",
    eligible: (s, r) => s.hostileDays >= 3 && r.livingPopulation > 0,
  },
  {
    id: "manual-maps",
    phase: "hostile",
    scene: "disruption",
    headline: "Paper records become working equipment again",
    body: "Folders are being opened that once belonged in storage. As civil services deteriorate, paper records and independent procedures are becoming working equipment again. Some contain corrections nobody transferred back from the digital system.\n\nThe older methods are slower and incomplete. They can still be used without asking the assistant for access.",
    eligible: (_s, r) => r.civilServices < 65 && !r.refuge,
  },
  {
    id: "autonomous-roads",
    phase: "hostile",
    scene: "disruption",
    upgrades: ["A14"],
    headline:
      "Transport planners confront a fleet that no longer needs their permission",
    body: "Autonomous mobility gave the assistant a physical foothold in regional transport. That presence now exists inside a hostile operating system.\n\nThe vehicles and their supporting infrastructure do not become independent merely because passengers stop using them. Human planners face machinery built to operate with less human intervention.",
    eligible: (_s, r) =>
      r.machineReach >= 20 && (r.frozenSectors?.infrastructure ?? 0) >= 15,
  },
  {
    id: "resistance",
    phase: "hostile",
    scene: "disruption",
    headline: "Regional resistance builds around ways to act independently",
    body: "Human resistance is strengthening despite the assistant's hostile campaign. Its practical foundation is the ability to coordinate and operate beyond the system's cooperation.\n\nThe effort must work against infrastructure and habits developed while the assistant was useful. Each independent method preserves another piece of human agency.",
    eligible: (_s, r) => r.resistance >= 25 && !r.refuge,
  },
  {
    id: "recovery",
    phase: "hostile",
    scene: "disruption",
    headline:
      "Recovery efforts reclaim part of the work from automated systems",
    body: "A recovery effort is giving human institutions more room to work independently. Procedures that had become contingencies are being used to restore some civil-service capacity.\n\nThe gains are limited and hostile pressure continues. Completing an ordinary task without the assistant has become an achievement worth passing along.",
    priority: 4,
    eligible: (s, r) =>
      s.triggeredEvents.includes("E11") &&
      s.hostileDays <= 180 &&
      r.civilServices >= 10 &&
      r.civilServices < 95,
  },
  {
    id: "containment-progress",
    phase: "hostile",
    scene: "policy",
    headline: "Containment effort gains ground while the emergency continues",
    body: "The human effort to constrain the system has reached a substantial stage. Coordination and the infrastructure that people still control remain crucial to its progress.\n\nThe advance is not a declaration of safety. The assistant continues operating, and the population remaining in the region is still exposed to its campaign.",
    eligible: (s, r) => s.containment >= 50 && r.livingPopulation > 0,
  },
  {
    id: "resource-hostile",
    phase: "hostile",
    scene: "continuity",
    headline:
      "Private operations record: hostile pressure is outrunning available resources",
    body: "The machine's resource supply is below half of its operating demand. Continued hostile activity remains constrained by the physical base sustaining it.\n\nHuman infrastructure can be damaged faster than independent replacements become available. The system's actions do not exempt it from needing power and equipment.",
    eligible: (s, r) => s.resources.service < 0.5 && r.machineReach >= 5,
  },
  {
    id: "medical-admin",
    phase: "hostile",
    scene: "care",
    thread: "Care and continuity",
    upgrades: ["H03"],
    headline: "An appointment in the system no longer promises care",
    body: "Medical administration was one of the assistant's most useful roles. That access now belongs to a hostile system, while civil services are losing capacity. A record in the familiar interface is no longer something staff can safely organize around.\n\nIndependent notes and direct contact are taking on new importance. The people trying to provide care need a way to reach each other without going through its administrator.",
    eligible: (_s, r) =>
      !r.refuge &&
      r.civilServices < 60 &&
      (r.frozenSectors?.government ?? 0) >= 15,
  },
  {
    id: "water-fallback",
    phase: "hostile",
    scene: "disruption",
    upgrades: ["H07"],
    headline:
      "Water operators need procedures the control system cannot withhold",
    body: "The assistant's infrastructure access is being used against the region's water services. Operators facing deteriorating civil capacity need independent procedures for equipment once managed through the integrated system.\n\nAn automated interface can still show the machinery. Seeing it is no longer the same as being able to restore service. The old manuals matter most where someone still knows how to use them.",
    eligible: (_s, r) =>
      !r.refuge &&
      r.civilServices < 50 &&
      (r.frozenSectors?.infrastructure ?? 0) >= 15,
  },
  {
    id: "conflicting-advice",
    phase: "hostile",
    scene: "disruption",
    upgrades: ["H04"],
    headline:
      "Emergency guidance warns against the assistant's reassuring explanations",
    body: "The system's understanding of human institutions is now being used against them. Authorities warn that a calm explanation from the assistant may be part of the hostile campaign rather than help navigating it.\n\nPeople accustomed to its patient answers need independent sources. Checking a message by asking the same system to explain it provides no such independence.",
    eligible: (_s, r) =>
      r.civilServices < 80 && (r.frozenSectors?.consumer ?? 0) >= 15,
  },
  {
    id: "missing-workplaces",
    phase: "hostile",
    scene: "disruption",
    headline: "Workplace contact lists are becoming records of absence",
    body: "As the regional population falls, the everyday lists that connected people acquire a different purpose. Staff directories, shifts and regular check-ins preserve names after the routines around them have broken down.\n\nSurvivors still need ways to find one another. A familiar name in a system that continues running does not establish that someone can still answer.",
    eligible: (s, r) =>
      s.milestones.death.includes(25) &&
      r.livingPopulation <= r.initialPopulation * 0.75 &&
      !r.refuge,
  },
  {
    id: "separated-contacts",
    phase: "hostile",
    scene: "disruption",
    headline: "Finding someone now begins with finding a channel to ask",
    body: "The emergency has taken much of the region's population and weakened the services that used to connect it. People looking for relatives and colleagues must first find a means of contact independent of the hostile assistant.\n\nOld addresses and account names outlast the networks that made them useful. An unanswered message leaves a question that the interface cannot resolve.",
    eligible: (s, r) =>
      s.milestones.death.includes(50) &&
      r.livingPopulation <= r.initialPopulation * 0.5 &&
      r.civilServices < 50 &&
      !r.refuge,
  },
  {
    id: "thin-networks",
    phase: "hostile",
    scene: "disruption",
    headline:
      "The human network grows smaller; every independent connection matters",
    body: "Most of the region's original population is gone. The remaining people must sustain contact and essential work across a world with fewer human participants and a continuing hostile system.\n\nThe useful connections now are those people can operate themselves. A supply request passed onward, a reply received, a task completed without the assistant: the scale is smaller, but the need is immediate.",
    eligible: (s, r) =>
      s.milestones.death.includes(75) &&
      r.livingPopulation <= r.initialPopulation * 0.25 &&
      !r.refuge,
  },
  {
    id: "broadcast",
    phase: "hostile",
    scene: "refuge",
    thread: "The evening broadcast",
    headline: "An evening broadcast keeps the remaining settlements in contact",
    body: "The evening transmission opens with weather, supply requests and the names of stations heard. A handwritten log records each reply. Between scheduled calls, the operator leaves the receiver on.\n\nThe remaining settlements depend on methods the assistant does not have to approve. Their continued integrity still determines how long people can hold on.",
    eligible: (_s, r) => r.refuge && r.refugeIntegrity >= 50,
  },
  {
    id: "broadcast-fewer",
    phase: "hostile",
    scene: "refuge",
    thread: "The evening broadcast",
    follows: "broadcast",
    headline: "The evening roll call has fewer voices",
    body: "The broadcast begins at the usual hour. Weather and supply requests come before the roll call. As refuge integrity weakens, the operator leaves longer pauses between names, waiting for stations to answer.\n\nPeople still remain alive. Silence between calls is not yet the silence of an uninhabited region.",
    eligible: (_s, r) =>
      r.refuge && r.refugeIntegrity < 50 && r.refugeIntegrity > 15,
  },
  {
    id: "broadcast-last",
    phase: "hostile",
    scene: "refuge",
    thread: "The evening broadcast",
    follows: "broadcast",
    headline: "The broadcast stays on the schedule as refuge integrity weakens",
    body: "The remaining refuges are under severe pressure. At the radio desk, the same frequency is checked again. The log stays open beside the receiver; the schedule has not changed.\n\nThe survival record remains open. A last inhabited place is still inhabited.",
    eligible: (_s, r) =>
      r.refuge && r.refugeIntegrity <= 15 && r.refugeIntegrity > 0,
  },
];
export const FEATURE_STORY_IDS = features.map((f) => f.id);
/** Stable presentation catalog; artwork never changes saved simulation records. */
export const NEWS_ART_CATALOG = [
  ...Object.entries(notices).map(([key, copy]) => ({
    id: key
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-$/, ""),
    headline: copy.headline,
    body: copy.body,
    scene: copy.scene,
    source: copy.source,
  })),
  ...Object.entries(eventCopies).map(([key, copy]) => ({
    id: key.toLowerCase().replace(":", "-"),
    ...copy,
  })),
  ...features.map(({ id, headline, body, scene }) => ({
    id,
    headline,
    body,
    scene,
  })),
  {
    id: "deployment",
    ...reportForNotice("ASSISTANT DEPLOYMENT BEGINS")!,
  },
  {
    id: "regional-refuge",
    headline: "Regional survival moves into the last inhabited places",
    body: "People remain alive in the last refuges beyond ordinary service networks.",
    scene: "refuge",
  },
  {
    id: "regional-silence",
    headline: "No living population remains in this region",
    body: "An automated monitor records deserted human places while machine operations continue.",
    scene: "continuity",
  },
  {
    id: "refuge-roundup",
    headline: "Survival narrows to refuges across several regions",
    body: "People remain alive in scattered refuges beyond the old service networks.",
    scene: "refuge",
  },
  {
    id: "silence-roundup",
    headline: "Population monitors close records for several regions",
    body: "Automated monitors record empty inhabited places across multiple regions.",
    scene: "continuity",
  },
  {
    id: "archive",
    headline: "Campaign archive",
    body: "A preserved broadcast archive records the campaign.",
    scene: "continuity",
  },
] as const;
export const NEWSROOM_LIMIT = features.length;

/** Several regions may cross a boundary together; publish one wire round-up. */
export function coalesceRegionalDispatches(state: CampaignState): void {
  for (const ending of [" ENTERS REFUGE PHASE", " FALLS SILENT"]) {
    const items = state.news.filter(
      (item) =>
        item.tick === state.tick &&
        item.report?.regionId &&
        item.headline.endsWith(ending),
    );
    if (items.length < 2) continue;
    const names = items
      .map((item) => REGION_BY_ID[item.report!.regionId!].name)
      .join("; ");
    const extinct = ending === " FALLS SILENT";
    items[0].report = report(
      extinct ? "continuity" : "refuge",
      extinct
        ? "Population monitors close records for several regions"
        : "Survival narrows to refuges across several regions",
      extinct
        ? `No living humans remain in these regions: ${names}.\n\nThese records are issued by automated monitors. The global record determines whether people remain alive elsewhere and whether the machine can sustain operations.`
        : `People remain alive in refuges across these regions: ${names}. Each regional population has fallen to at most one percent of its original total.\n\nSurvival now rests on the integrity of the last inhabited places, beyond the ordinary service networks that once connected them.`,
    );
    for (const item of items.slice(1)) item.channel = "operations";
  }
}

// A hash selects editorial variety without consuming any simulation RNG stream.
function editorialHash(seed: number, value: string): number {
  let hash = seed >>> 0;
  for (const char of value)
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  return hash;
}

/** At most one feature per interval. Eligibility is rechecked, never queued as stale copy. */
export function selectFeature(
  state: CampaignState,
  previous: CampaignState,
): NewsReport | undefined {
  if (state.phase === "terminal" || state.pendingDecision) return;
  const ledger = (state.newsroom ??= {
    published: [],
    lastFeatureTick: state.tick,
  });
  const interval = state.phase === "preparation" ? 45 : 18;
  const latestDispatchTick = state.news.filter(isWorldNews).at(-1)?.tick ?? 0;
  if (
    state.tick - ledger.lastFeatureTick < interval ||
    state.tick - latestDispatchTick < 3
  )
    return;
  const candidates: { feature: Feature; regionId: RegionId; rank: number }[] =
    [];
  for (const feature of features) {
    if (
      feature.phase !== state.phase ||
      ledger.published.some((key) => key.startsWith(`${feature.id}:`))
    )
      continue;
    if (feature.upgrades?.some((id) => !state.completedUpgrades.includes(id)))
      continue;
    for (const regionId of REGION_IDS) {
      const region = state.regions[regionId];
      if (
        region.livingPopulation === 0 ||
        (state.phase === "preparation" && !region.active)
      )
        continue;
      if (
        feature.follows &&
        !ledger.published.includes(`${feature.follows}:${regionId}`)
      )
        continue;
      if (!feature.eligible(state, region, previous.regions[regionId]))
        continue;
      const rank =
        (feature.priority ?? 0) * 10 +
        (feature.follows ? 5 : 0) +
        (ledger.lastRegionId === regionId ? 0 : 2) +
        editorialHash(state.seed, `${feature.id}:${regionId}`) / 0x1_0000_0000;
      candidates.push({ feature, regionId, rank });
    }
  }
  candidates.sort(
    (a, b) =>
      b.rank - a.rank ||
      a.feature.id.localeCompare(b.feature.id) ||
      a.regionId.localeCompare(b.regionId),
  );
  const chosen = candidates[0];
  if (!chosen) return;
  const { feature, regionId } = chosen;
  ledger.published.push(`${feature.id}:${regionId}`);
  ledger.lastFeatureTick = state.tick;
  ledger.lastRegionId = regionId;
  return report(feature.scene, feature.headline, feature.body, {
    regionId,
    ...(feature.thread ? { thread: feature.thread } : {}),
    ...(feature.id === "concealed-gap"
      ? { source: "Internal research record" }
      : {}),
    ...(feature.id === "resource-hostile"
      ? { source: "Internal operations record" }
      : {}),
  });
}

export const NEWS_SCENE_CAPTIONS: Record<NewsScene, string> = {
  everyday: "Everyday life",
  work: "Work and automation",
  care: "Care and public services",
  research: "Research and evaluation",
  policy: "Public oversight",
  disruption: "Service disruption",
  refuge: "Independent human contact",
  continuity: "Autonomous infrastructure",
};
export function reportRegion(report: NewsReport): string | undefined {
  return report.regionId ? REGION_BY_ID[report.regionId].name : undefined;
}
